/**
 * The alert list's filter bar — search, severity, camera, time window and rows-per-page in
 * one row, shared by the local and the cloud list so the two can't offer different filters.
 *
 * Every control here narrows the SAME server-side query the list runs; nothing filters in the
 * browser. That matters because paging is server-side too: a client-side filter would hide
 * rows from page 1 while leaving them counted in "148 alerts", which is the bug this bar is
 * shaped to avoid.
 *
 * Status is deliberately NOT here — it's the tab strip above the table, because status is the
 * one axis an operator flips constantly while working a queue and a tab keeps its count visible.
 *
 * ALERT TYPE is not in the row either, and for a different reason: it is a CHIP ROW below,
 * drawn by the alert board's own `HexFilterBar`. Not a lookalike — the same component, so
 * "Fire Detected · 14" is the same control in the same shape whether the operator is looking
 * at the board or at the history, and narrowing one then opening the other is the same gesture
 * twice rather than two dialects of one question. It also earns the room a dropdown saves: a
 * closed select hides the type names, and WHICH types a site actually raises is most of what
 * an operator wants to know on arriving.
 */
import { useMemo } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import HexFilterBar, { type FilterGroup } from '../hexagon/HexFilterBar';
import { ALL_ALERTS } from '../../hooks/useAlertTypeFilter';
import { FilterChips, FilterPick, FilterSearch, FilterSection } from '../layout/FilterColumn';
import { BTN_GHOST, SEARCH_INPUT } from '../common/ui';

/** The window the list looks back over. `hours: null` = no lower bound. */
export const ALERT_RANGES = [
  { key: 'all', label: 'All time', hours: null },
  { key: '1h', label: 'Last hour', hours: 1 },
  { key: '24h', label: 'Last 24 hours', hours: 24 },
  { key: '7d', label: 'Last 7 days', hours: 24 * 7 },
  { key: '30d', label: 'Last 30 days', hours: 24 * 30 },
] as const;

export type AlertRangeKey = (typeof ALERT_RANGES)[number]['key'];
/** The chip labels in the filter column — the full label is the chip's title. */
const RANGE_SHORT: Record<AlertRangeKey, string> = { all: 'All time', '1h': '1 hour', '24h': '24 hours', '7d': '7 days', '30d': '30 days' };

/**
 * A range key → the `from` the API wants: epoch SECONDS, matching `alerts.event_timestamp`.
 * Returns undefined for "all time", which the caller should then leave off the request.
 */
export function alertRangeFrom(key: AlertRangeKey): number | undefined {
  const hours = ALERT_RANGES.find((r) => r.key === key)?.hours;
  if (!hours) return undefined;
  return Math.floor(Date.now() / 1000) - hours * 3600;
}

/** Rows-per-page choices. Capped at 100 — the API clamps anything larger. */
export const ALERT_PAGE_SIZES = [10, 25, 50, 100];

const CONTROL =
  'h-9 rounded-lg border border-border bg-input px-2.5 text-[13px] text-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-ring';

export interface AlertListFiltersProps {
  search: string;
  onSearchChange: (v: string) => void;
  severity: string;
  onSeverityChange: (v: string) => void;
  /**
   * The alert's NAME — "Blacklisted Person", "Fire", "Vehicle Crossing" — as the card shows
   * it. The same axis the alert board's chip row filters on, so an operator who narrowed the
   * board to one kind and came here to read the history asks the identical question.
   *
   * By name and not by `event_type`, because the type cannot express it: "Blacklisted Person"
   * and "Face Recognized" are both `face`, and they are the two an operator most needs apart.
   */
  alertName: string;
  onAlertNameChange: (v: string) => void;
  /**
   * The names that actually occur under the OTHER filters, with counts — from the node, which
   * derives them with the same expression it filters by (see `DISPLAY_NAME_SQL`). Offering a
   * name the query cannot match would give an empty page that reads as "there were none".
   */
  alertNames: { name: string; count: number }[];
  cameraId: string;
  onCameraChange: (v: string) => void;
  /** Every camera that can be filtered on, `{ id, name }`. */
  cameras: { id: string; name?: string }[];
  range: AlertRangeKey;
  onRangeChange: (v: AlertRangeKey) => void;
  pageSize: number;
  onPageSizeChange: (v: number) => void;
  /** Shown only when something is actually filtered. */
  onClear?: () => void;
  /**
   * `bar` (default) is the card-plus-chip-row above the list. `column` renders the same
   * controls as stacked sections for a page's `<FilterColumn>` (which owns "Clear"), the type
   * chips becoming a radio list with the same counts.
   */
  variant?: 'bar' | 'column';
}

export default function AlertListFilters({
  search, onSearchChange,
  severity, onSeverityChange,
  alertName, onAlertNameChange,
  alertNames,
  cameraId, onCameraChange,
  cameras,
  range, onRangeChange,
  pageSize, onPageSizeChange,
  onClear,
  variant = 'bar',
}: AlertListFiltersProps) {
  const dirty = !!(search || severity || alertName || cameraId || range !== 'all');

  /**
   * The type chips, in the board's own shape. `effect: 'hide'` because here the misses are
   * genuinely GONE — the query never returned them — where the board leaves its cameras in
   * place and greys them. The bar words its result line from that, so getting it wrong would
   * send the reader hunting for dimmed rows that do not exist.
   */
  const typeGroup = useMemo<FilterGroup>(() => ({
    label: 'Alert type',
    value: alertName || ALL_ALERTS,
    effect: 'hide',
    onChange: (key) => onAlertNameChange(key === ALL_ALERTS ? '' : key),
    chips: [
      { key: ALL_ALERTS, label: 'All types', count: alertNames.reduce((n, a) => n + a.count, 0) },
      ...alertNames.map((a) => ({
        key: a.name,
        label: a.name,
        count: a.count,
        title: `Show only "${a.name}" — ${a.count} alert${a.count === 1 ? '' : 's'}`,
      })),
      // A name chosen before its last alert aged out of the window stays on the row rather
      // than vanishing under the operator while the list is still filtered by it.
      ...(alertName && !alertNames.some((a) => a.name === alertName)
        ? [{ key: alertName, label: alertName, count: 0 }]
        : []),
    ],
  }), [alertName, alertNames, onAlertNameChange]);

  if (variant === 'column') {
    // The Search & Track panel's controls: a long list (alert names, cameras) is a row that opens
    // a searchable list; a few short choices are one-click chips.
    const cameraOptions = [
      ...cameras.map((c) => ({ value: c.id, label: c.name || c.id.slice(0, 8) })),
      // An id that arrived in the URL from a camera tile may not be in the list (a deleted
      // camera, or a list that failed to load) — keep it choosable.
      ...(cameraId && !cameras.some((c) => c.id === cameraId) ? [{ value: cameraId, label: `${cameraId.slice(0, 8)}…` }] : []),
    ];
    return (
      <>
        <FilterSearch value={search} onChange={onSearchChange} placeholder="Event, alert name or description…" />

        <FilterSection title="Alert type">
          <div data-tour="alerts-filters">
            <FilterPick
              label="Alert type"
              allLabel="All types"
              allCount={alertNames.reduce((n, a) => n + a.count, 0)}
              value={alertName}
              onChange={onAlertNameChange}
              options={alertNames.map((a) => ({ value: a.name, label: a.name, count: a.count }))}
              searchPlaceholder="Search alert types…"
            />
          </div>
        </FilterSection>

        <FilterSection title="Severity">
          <FilterChips cols={2} value={severity} onChange={onSeverityChange} options={[
            { value: '', label: 'All' },
            { value: 'critical', label: 'Critical' },
            { value: 'warning', label: 'Warning' },
            { value: 'normal', label: 'Normal' },
          ]} />
        </FilterSection>

        <FilterSection title="Camera">
          <FilterPick label="Camera" allLabel="All cameras" value={cameraId} onChange={onCameraChange} options={cameraOptions} inlineMax={0} searchPlaceholder="Search cameras…" />
        </FilterSection>

        <FilterSection title="When">
          <FilterChips<AlertRangeKey> value={range} onChange={onRangeChange} options={ALERT_RANGES.map((r) => ({ value: r.key, label: RANGE_SHORT[r.key], title: r.label }))} />
        </FilterSection>

        <FilterSection title="Rows per page">
          <FilterChips<number> cols={4} value={pageSize} onChange={onPageSizeChange} options={ALERT_PAGE_SIZES.map((n) => ({ value: n, label: String(n) }))} />
        </FilterSection>
      </>
    );
  }

  return (
    <>
    <div className="rounded-xl border border-border bg-card p-3 shadow-card">
      <div className="flex flex-wrap items-center gap-2">
        {/* Search — the widest control, because it's the one you type into. Filtering is
            debounced by the caller, so there's no submit button to hunt for. */}
        <div className="relative min-w-[13rem] flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search event, alert name or description…"
            aria-label="Search alerts"
            className={`${SEARCH_INPUT} pr-8`}
          />
          {search && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <select
          value={severity}
          onChange={(e) => onSeverityChange(e.target.value)}
          aria-label="Severity"
          className={CONTROL}
        >
          <option value="">All severities</option>
          <option value="critical">Critical</option>
          <option value="warning">Warning</option>
          <option value="normal">Normal</option>
        </select>

        <select
          value={cameraId}
          onChange={(e) => onCameraChange(e.target.value)}
          aria-label="Camera"
          className={`${CONTROL} max-w-[12rem]`}
        >
          <option value="">All cameras</option>
          {/* An id that arrived in the URL from a camera tile may not be in the list (deleted
              camera, or a list that failed to load) — keep it selectable rather than silently
              snapping the dropdown back to "All cameras" while the query still filters. */}
          {cameraId && !cameras.some((c) => c.id === cameraId) && (
            <option value={cameraId}>{cameraId.slice(0, 8)}…</option>
          )}
          {cameras.map((c) => (
            <option key={c.id} value={c.id}>{c.name || c.id.slice(0, 8)}</option>
          ))}
        </select>

        <select
          value={range}
          onChange={(e) => onRangeChange(e.target.value as AlertRangeKey)}
          aria-label="Time range"
          className={CONTROL}
        >
          {ALERT_RANGES.map((r) => (
            <option key={r.key} value={r.key}>{r.label}</option>
          ))}
        </select>

        {dirty && onClear && (
          <button
            type="button"
            onClick={onClear}
            className={BTN_GHOST}
          >
            Clear
          </button>
        )}

        {/* Rows per page. Not wrapped in a <label> — a label around a native select
            re-dispatches the click to the control in some Chromium builds, which opens the
            dropdown and closes it again so the choice never lands. */}
        <span className="ml-auto flex items-center gap-1.5 whitespace-nowrap text-[13px] text-muted-foreground">
          Rows
          <select
            value={String(pageSize)}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            aria-label="Rows per page"
            className={CONTROL}
          >
            {ALERT_PAGE_SIZES.map((n) => <option key={n} value={String(n)}>{n}</option>)}
          </select>
        </span>
      </div>
    </div>

    {/* Below the card, not inside it: the board draws this bar standing on the page, and a
        bordered well nested in another card reads as a panel within a panel. `HexFilterBar`
        returns null on its own when there is nothing to choose between. */}
    <HexFilterBar groups={[typeGroup]} noun="alerts" />
    </>
  );
}
